import { useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { Draft, EncoderEvent } from '../components/Reader/Encoder/types'

interface UseEncoderDraftResult {
    saveDraft: (body: string) => void
    deleteDraft: () => Promise<void>
}

export function useEncoderDraft(
    encodingId: string,
    userId: string | undefined,
    dispatch: React.Dispatch<EncoderEvent>,
    debounceMs = 500
): UseEncoderDraftResult {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const currentDraftIdRef = useRef<string | null>(null)

    // Load existing draft on mount
    useEffect(() => {
        if (!userId) return

        const loadDraft = async () => {
            const { data, error } = await supabase
                .from('etcbc_encoding_drafts')
                .select('*')
                .eq('encoding_id', encodingId)
                .eq('user_id', userId)
                .single()

            if (!error && data) {
                currentDraftIdRef.current = data.id
                dispatch({
                    type: 'DRAFT_LOADED',
                    draft: data as Draft,
                })
            }
        }

        loadDraft()
    }, [encodingId, userId, dispatch])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const saveDraft = useCallback(
        (body: string) => {
            if (!userId) return

            // Clear previous timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            // Debounce the save
            timeoutRef.current = setTimeout(async () => {
                try {
                    // Upsert the draft
                    const { data, error } = await supabase
                        .from('etcbc_encoding_drafts')
                        .upsert(
                            {
                                encoding_id: encodingId,
                                user_id: userId,
                                body,
                            },
                            {
                                onConflict: 'encoding_id,user_id',
                            }
                        )
                        .select()
                        .single()

                    if (!error && data) {
                        currentDraftIdRef.current = data.id
                        dispatch({
                            type: 'DRAFT_SAVED',
                            draftId: data.id,
                        })
                    }
                } catch {
                    // Silently fail - draft saving is not critical
                }
            }, debounceMs)
        },
        [encodingId, userId, dispatch, debounceMs]
    )

    const deleteDraft = useCallback(async () => {
        if (!userId) return

        // Clear any pending save
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        try {
            await supabase
                .from('etcbc_encoding_drafts')
                .delete()
                .eq('encoding_id', encodingId)
                .eq('user_id', userId)

            currentDraftIdRef.current = null
            dispatch({ type: 'DRAFT_DELETED' })
        } catch {
            // Silently fail
        }
    }, [encodingId, userId, dispatch])

    return { saveDraft, deleteDraft }
}
